import React, { useEffect, useRef, useState } from 'react';
import newLeaseModalStyles from '../../../stylesheets/management_site/newLeaseModal.module.scss';
import scheduleFormStyles from '../../../stylesheets/management_site/scheduleForm.module.scss';
import classNames from 'classnames';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

const ScheduleFrequency = ({
  scheduleType,
  setScheduleType,
  fieldPrefix,
  monthlyDayEnd,
  setMonthlyDayEnd,
  parsedScheduleOptions = null,
}) => {
  const END_OF_MONTH = 'endOfMonth';
  const SPECIFY = 'specify';

  const nthToNumber = {
    first: 1,
    second: 2,
    third: 3,
    fourth: 4,
    last: 5,
  };

  const weekdayToNumber = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  const monthlyNth = nthToNumber[parsedScheduleOptions?.monthlyOptions?.nth];
  const monthlyWeekday =
    weekdayToNumber[parsedScheduleOptions?.monthlyOptions?.weekday];
  const monthlyDayOfMonth = parsedScheduleOptions?.monthlyOptions?.dayOfMonth;
  const monthlyEveryNMonths =
    parsedScheduleOptions?.monthlyOptions?.everyNMonths;
  const weeklyEveryNWeeks = parsedScheduleOptions?.weeklyOptions?.everyNWeeks;
  const weeklyWeekday =
    parsedScheduleOptions?.weeklyOptions?.weekday?.toLowerCase();
  const dailyEveryNDays = parsedScheduleOptions?.dailyOptions?.everyNDays;
  const dailyEveryWeekday = parsedScheduleOptions?.dailyOptions?.everyWeekday;

  const days = [
    ...Array.from({ length: 28 }, (_, i) => ({
      label: `${i + 1}${['st', 'nd', 'rd'][((i + 1) % 10) - 1] || 'th'}`,
      value: i + 1,
    })),
    { label: 'End of Month', value: END_OF_MONTH },
  ];

  const weeks = [
    { label: 'First', value: 1 },
    { label: 'Second', value: 2 },
    { label: 'Third', value: 3 },
    { label: 'Fourth', value: 4 },
    { label: 'Last', value: 5 },
  ];

  const weekdays = [
    { label: 'Sunday', value: 0 },
    { label: 'Monday', value: 1 },
    { label: 'Tuesday', value: 2 },
    { label: 'Wednesday', value: 3 },
    { label: 'Thursday', value: 4 },
    { label: 'Friday', value: 5 },
    { label: 'Saturday', value: 6 },
  ];

  const weeklyCheckboxes = [
    ['S', 'weekly_su', 'Sunday'],
    ['M', 'weekly_m', 'Monday'],
    ['T', 'weekly_tu', 'Tuesday'],
    ['W', 'weekly_w', 'Wednesday'],
    ['T', 'weekly_th', 'Thursday'],
    ['F', 'weekly_f', 'Friday'],
    ['S', 'weekly_sa', 'Saturday'],
  ];
  const prefixed = (suffix) => `${fieldPrefix}_${suffix}`; // Needed for fill_out_form in tests
  const selectRef = useRef(null);

  const [monthlySubType, setMonthlySubType] = useState(() => {
    if (monthlyNth && monthlyWeekday) {
      return 'monthly_weekday';
    }
    return 'monthly';
  });
  const [dailySubType, setDailySubType] = useState(() => {
    if (dailyEveryWeekday === true) {
      return 'all';
    }
    return 'daily';
  });

  const [selectedWeeklyDays, setSelectedWeeklyDays] = useState(() => {
    const initialDays = [];
    if (weeklyWeekday) {
      const dayIndex = weeklyCheckboxes.findIndex(
        ([, , fullName]) => fullName.toLowerCase() === weeklyWeekday
      );
      if (dayIndex !== -1) {
        initialDays.push(weeklyCheckboxes[dayIndex][1]);
      }
    }
    return initialDays;
  });

  useEffect(() => {
    if (selectRef.current && window.$ && window.$.fn.select2) {
      const select = window.$(selectRef.current);
      select.select2();
      select.on('change', (e) => {
        setScheduleType(e.target.value);
      });

      return () => {
        select.off('change');
        select.select2('destroy');
      };
    }
  }, [setScheduleType]);

  return (
    <div className={newLeaseModalStyles.scheduleFrequencySection}>
      <div className={newLeaseModalStyles.sectionHeader}>
        <h3 className={newLeaseModalStyles.sectionTitle}>Schedule Frequency</h3>
      </div>
      <div className={newLeaseModalStyles.sectionContent}>
        <div
          id="schedule-frequency-container"
          className={classNames(
            'pm-flex',
            'pm-flex-col',
            newLeaseModalStyles.noMarginBottom,
            scheduleFormStyles.alignScheduleContent
          )}
        >
          <div className="pm-flex pm-form-multi">
            <span className={scheduleFormStyles.frequencyLabel}>
              Frequency:
            </span>
            <div
              className={classNames(
                'mdc-form-field',
                scheduleFormStyles.frequencySelectContainer
              )}
            >
              <div
                className={classNames(
                  'pm-select',
                  scheduleFormStyles.fullWidthSelect
                )}
              >
                <select
                  ref={selectRef}
                  id={`${fieldPrefix}_schedule_type`}
                  className={classNames(
                    'pm-default-select2-menu',
                    'mdc-select',
                    'pm-select-medium',
                    'schedule_type_button',
                    scheduleFormStyles.frequencySelect
                  )}
                  name={`${fieldPrefix}[schedule_type]`}
                  defaultValue={scheduleType}
                >
                  {['monthly', 'weekly', 'daily', 'once'].map((type) => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          {scheduleType === 'monthly' && (
            <div className="hidden-select-fields monthly-fields">
              <h4 className={scheduleFormStyles.optionsTitle}>
                Monthly Options
              </h4>
              <div
                className={classNames(
                  'pm-flex',
                  'pm-form-multi',
                  'pm-margin-bottom-8',
                  {
                    [scheduleFormStyles.selected]: monthlySubType === 'monthly',
                    [scheduleFormStyles.notSelected]:
                      monthlySubType !== 'monthly',
                  }
                )}
              >
                <div className="mdc-form-field">
                  <div className="mdc-radio">
                    <input
                      type="radio"
                      id={`${fieldPrefix}_schedule_sub_type_monthly`}
                      className="mdc-radio__native-control"
                      name={`${fieldPrefix}[schedule_sub_type]`}
                      value="monthly"
                      defaultChecked={
                        monthlyDayOfMonth
                          ? true
                          : !(monthlyNth && monthlyWeekday)
                      }
                      onChange={() => setMonthlySubType('monthly')}
                    />
                    <div className="mdc-radio__background">
                      <div className="mdc-radio__outer-circle" />
                      <div className="mdc-radio__inner-circle" />
                    </div>
                  </div>
                  <label htmlFor={`${fieldPrefix}_schedule_sub_type_monthly`}>
                    The
                  </label>
                </div>
                <div className="mdc-form-field">
                  <div className="pm-select">
                    <input
                      type="hidden"
                      id={prefixed('monthly_day_end')}
                      name={`${fieldPrefix}[monthly_day_end]`}
                      value={monthlyDayEnd}
                    />
                    <select
                      id={prefixed('monthly_day')}
                      className="pm-default-select2-menu mdc-select pm-select-small"
                      name={`${fieldPrefix}[monthly_day]`}
                      onChange={(e) =>
                        setMonthlyDayEnd(
                          e.target.value === END_OF_MONTH
                            ? END_OF_MONTH
                            : SPECIFY
                        )
                      }
                      defaultValue={monthlyDayOfMonth || null}
                    >
                      {days.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div
                className={classNames(
                  'pm-flex',
                  'pm-form-multi',
                  'pm-margin-bottom-8',
                  {
                    [scheduleFormStyles.selected]:
                      monthlySubType === 'monthly_weekday',
                    [scheduleFormStyles.notSelected]:
                      monthlySubType !== 'monthly_weekday',
                  }
                )}
              >
                <div className="mdc-form-field">
                  <div className="mdc-radio">
                    <input
                      type="radio"
                      id={`${fieldPrefix}_schedule_sub_type_monthly_weekday`}
                      className="mdc-radio__native-control"
                      name={`${fieldPrefix}[schedule_sub_type]`}
                      value="monthly_weekday"
                      defaultChecked={monthlyNth && monthlyWeekday}
                      onChange={() => setMonthlySubType('monthly_weekday')}
                    />
                    <div className="mdc-radio__background">
                      <div className="mdc-radio__outer-circle" />
                      <div className="mdc-radio__inner-circle" />
                    </div>
                  </div>
                  <label
                    htmlFor={`${fieldPrefix}_schedule_sub_type_monthly_weekday`}
                  >
                    The
                  </label>
                </div>
                <div className="mdc-form-field">
                  <div className="pm-select">
                    <select
                      id={prefixed('monthly_week')}
                      className="pm-default-select2-menu mdc-select pm-select-small"
                      name={`${fieldPrefix}[monthly_week]`}
                      defaultValue={monthlyNth || null}
                    >
                      {weeks.map((w) => (
                        <option key={w.value} value={w.value}>
                          {w.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mdc-form-field">
                  <div className="pm-select">
                    <select
                      id={prefixed('monthly_weekday')}
                      className="pm-default-select2-menu mdc-select pm-select-small"
                      name={`${fieldPrefix}[monthly_weekday]`}
                      defaultValue={monthlyWeekday || null}
                    >
                      {weekdays.map((wd) => (
                        <option key={wd.value} value={wd.value}>
                          {wd.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div
                className={classNames(
                  'pm-flex',
                  'pm-form-multi',
                  'pm-margin-bottom-8',
                  'pm-align-center',
                  scheduleFormStyles.monthlyOptionsContainer
                )}
              >
                <span className={scheduleFormStyles.everyLabel}>
                  ... of every
                </span>
                <div
                  className={classNames(
                    'mdc-form-field',
                    'mdc-form-field--align-end',
                    scheduleFormStyles.textInputContainer
                  )}
                >
                  <div
                    className={classNames(
                      'mdc-text-field',
                      'pm-margin-none',
                      scheduleFormStyles.textInput
                    )}
                    data-mdc-auto-init="MDCTextField"
                  >
                    <input
                      type="text"
                      className="mdc-text-field__input"
                      name={`${fieldPrefix}[monthly_months]`}
                      defaultValue={monthlyEveryNMonths?.toString() || '1'}
                    />
                  </div>
                </div>
                <span className={scheduleFormStyles.monthsLabel}>month(s)</span>
              </div>
            </div>
          )}

          {scheduleType === 'weekly' && (
            <div className="hidden-select-fields weekly-fields">
              <h4 className={scheduleFormStyles.optionsTitle}>
                Weekly Options
              </h4>
              <div className="pm-flex pm-form-multi pm-margin-bottom-8 pm-align-center">
                <span className={scheduleFormStyles.everyLabel}>Every</span>
                <div
                  className={classNames(
                    'mdc-form-field',
                    'mdc-form-field--align-end',
                    scheduleFormStyles.textInputContainer
                  )}
                >
                  <div
                    className={classNames(
                      'mdc-text-field',
                      'pm-margin-none',
                      scheduleFormStyles.textInput
                    )}
                    data-mdc-auto-init="MDCTextField"
                  >
                    <input
                      type="text"
                      className="mdc-text-field__input"
                      name={`${fieldPrefix}[weekly_weeks]`}
                      defaultValue={weeklyEveryNWeeks?.toString() || '1'}
                    />
                  </div>
                </div>
                <span className={scheduleFormStyles.weeksOnLabel}>
                  week(s) on:
                </span>
              </div>
              <div className={scheduleFormStyles.weeklyDayContainer}>
                <ToggleButtonGroup
                  value={selectedWeeklyDays}
                  onChange={(event, newSelectedDays) => {
                    setSelectedWeeklyDays(newSelectedDays);
                  }}
                  aria-label="weekly days"
                  size="small"
                  className={scheduleFormStyles.weeklyToggleButtonGroup}
                >
                  {weeklyCheckboxes.map(([abbreviation, name, fullName]) => (
                    <ToggleButton
                      key={name}
                      value={name}
                      aria-label={fullName}
                      className={scheduleFormStyles.weeklyDayMui}
                    >
                      {abbreviation}
                      <input
                        type="hidden"
                        name={`${fieldPrefix}[${name}]`}
                        value={selectedWeeklyDays.includes(name) ? '1' : '0'}
                      />
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </div>
            </div>
          )}
          {scheduleType === 'daily' && (
            <div className="hidden-select-fields daily-fields">
              <h4 className={scheduleFormStyles.optionsTitle}>Daily Options</h4>
              <div
                className={classNames(
                  'pm-flex',
                  'pm-form-multi',
                  'pm-margin-bottom-8',
                  'pm-align-center',
                  {
                    [scheduleFormStyles.selected]: dailySubType === 'daily',
                    [scheduleFormStyles.notSelected]: dailySubType !== 'daily',
                  }
                )}
              >
                <div
                  className={classNames(
                    'mdc-form-field',
                    scheduleFormStyles.textInputContainer
                  )}
                >
                  <div className="mdc-radio">
                    <input
                      type="radio"
                      id={`${fieldPrefix}_schedule_sub_type_daily`}
                      className="mdc-radio__native-control"
                      name={`${fieldPrefix}[schedule_sub_type]`}
                      value="daily"
                      defaultChecked={
                        dailyEveryNDays ? true : !dailyEveryWeekday
                      }
                      onChange={() => setDailySubType('daily')}
                    />
                    <div className="mdc-radio__background">
                      <div className="mdc-radio__outer-circle" />
                      <div className="mdc-radio__inner-circle" />
                    </div>
                  </div>
                  <label htmlFor={`${fieldPrefix}_schedule_sub_type_daily`}>
                    Every
                  </label>
                </div>
                <div
                  className={classNames(
                    'mdc-form-field',
                    'mdc-form-field--align-end',
                    scheduleFormStyles.textInputContainer
                  )}
                >
                  <div
                    className={classNames(
                      'mdc-text-field',
                      'pm-margin-none',
                      scheduleFormStyles.textInput
                    )}
                    data-mdc-auto-init="MDCTextField"
                  >
                    <input
                      type="text"
                      className="mdc-text-field__input"
                      name={`${fieldPrefix}[daily_day]`}
                      defaultValue={dailyEveryNDays?.toString() || '1'}
                    />
                  </div>
                </div>
                <span className={scheduleFormStyles.daysLabel}>days</span>
              </div>
              <div
                className={classNames(
                  'pm-flex',
                  'pm-form-multi',
                  'pm-margin-bottom-8',
                  {
                    [scheduleFormStyles.selected]: dailySubType === 'all',
                    [scheduleFormStyles.notSelected]: dailySubType !== 'all',
                  }
                )}
              >
                <div className="mdc-form-field">
                  <div className="mdc-radio">
                    <input
                      type="radio"
                      id={`${fieldPrefix}_schedule_sub_type_all`}
                      className="mdc-radio__native-control"
                      name={`${fieldPrefix}[schedule_sub_type]`}
                      value="all"
                      defaultChecked={dailyEveryWeekday === true}
                      onChange={() => setDailySubType('all')}
                    />
                    <div className="mdc-radio__background">
                      <div className="mdc-radio__outer-circle" />
                      <div className="mdc-radio__inner-circle" />
                    </div>
                  </div>
                  <label htmlFor={`${fieldPrefix}_schedule_sub_type_all`}>
                    Every weekday
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleFrequency;
